/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BiPlusCircle } from "react-icons/bi";
import { FaEdit } from "react-icons/fa";
import { MdDeleteSweep } from "react-icons/md";
import { Card, CardBody, Flex, Input } from "@chakra-ui/react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import { useUser } from "../../hooks/use-user";
import FilterBar from "../../components/filterBar";
import AppDropdown from "../../components/AppDropdown";
import { Paths } from "../../routes/paths";
import { useLookupCashCenters } from "../../services/lookups-queries";
import { HasPermission } from "../../utils/helper";
import { useDeleteAssets, useGetAssets } from "../../services/assets";
import ConfirmationModal from "../../components/confirmation-modal";
import { initialDataState } from "./constants";

export default function AssetsList() {
  const user = useUser();
  const [dataState, setDataState] = useState(initialDataState);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedCashCenter, setSelectedCashCenter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState(null);
  const pageSize = 25;
  const filterTimeout = useRef(null);

  const { mutate: getAssets, data: assetsList } = useGetAssets();
  const { data: cashCenterResponse, isPending: gettingCashCenter } =
    useLookupCashCenters();
  const { mutate: deleteAsset, isPending: deletingAsset } = useDeleteAssets();

  useEffect(() => {
    if (filter !== null) {
      filterTimeout.current = setTimeout(() => {
        setCurrentPage(1);
        getAssets({
          pageNumber: currentPage,
          pageRowCount: pageSize,
          searchCriteria: filter,
          operationDate: new Date(),
          cashCenterId: selectedCashCenter?.cashCenterId || "",
        });
        clearTimeout(filterTimeout.current);
        filterTimeout.current = null;
      }, 1000);
    }
    return () => {
      if (filterTimeout.current) {
        clearTimeout(filterTimeout.current);
        filterTimeout.current = null;
      }
    };
  }, [filter]);

  useEffect(() => {
    getAssets({
      pageNumber: currentPage,
      pageRowCount: pageSize,
      searchCriteria: filter,
      operationDate: new Date(),
      cashCenterId: selectedCashCenter?.cashCenterId || "",
    });
  }, []);

  const onDdlCashCentersChanged = (event) => {
    const value = event.target.value;

    if (value.cashCenterId === "0") {
      setSelectedCashCenter(null);
      getAssets({
        pageNumber: currentPage,
        pageRowCount: pageSize,
        searchCriteria: filter,
        operationDate: new Date(),
        cashCenterId: "",
      });
      return;
    }

    setSelectedCashCenter(value);

    const cashCenterId = value.cashCenterId || null;

    getAssets({
      pageNumber: currentPage,
      pageRowCount: pageSize,
      searchCriteria: filter,
      operationDate: new Date(),
      cashCenterId,
    });
  };

  const onFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const editAssetCell = ({ dataItem }) => {
    return (
      <td>
        <Flex
          as={Link}
          to={`${Paths.editAssets}/${dataItem.assetId}`}
          justifyContent="center"
          style={{ cursor: "pointer" }}
          w="100%"
          title="Edit Asset"
        >
          <FaEdit fontSize="18px" color="var(--primary-dark)" />
        </Flex>
      </td>
    );
  };

  const deleteAssetCell = ({ dataItem }) => {
    return (
      <td>
        <Flex
          justifyContent="center"
          w="100%"
          title="Delete Asset"
          cursor="pointer"
          onClick={() => {
            setSelectedAsset(dataItem);
            setShowConfirmationModal(true);
          }}
        >
          <MdDeleteSweep fontSize="18px" color="var(--primary-dark)" />
        </Flex>
      </td>
    );
  };

  const onPageChange = (e) => {
    const _page = e.page.skip / pageSize + 1;
    setCurrentPage(_page);
    getAssets({
      pageNumber: _page,
      pageRowCount: pageSize,
      searchCriteria: filter,
      operationDate: new Date(),
      cashCenterId: selectedCashCenter?.cashCenterId || "",
    });
  };

  const onAssetDelete = () => {
    deleteAsset(selectedAsset.assetId);
  };

  const canAddAsset = useMemo(() => {
    if (HasPermission(user, "Asset", "Add")) {
      return false;
    }
    return true;
  }, [user]);

  const canEdit = useMemo(() => {
    return HasPermission(user, "Asset", "Edit");
  }, [user]);

  const canDelete = useMemo(() => {
    return HasPermission(user, "Asset", "Delete");
  }, [user]);

  return (
    <Flex className="main-container">
      {showConfirmationModal ? (
        <ConfirmationModal
          show={showConfirmationModal}
          onConfirm={onAssetDelete}
          onClose={() => {
            setSelectedAsset(null);
            setShowConfirmationModal(false);
          }}
        />
      ) : (
        <></>
      )}

      <Card m="20px">
        <CardBody>
          <FilterBar
            heading="Assets"
            showLoader={gettingCashCenter || deletingAsset}
          >
            {canAddAsset ? (
              <></>
            ) : (
              <Link
                className="single-icon"
                to={Paths.addAssets}
                title="Add Asset"
                disabled={canAddAsset}
              >
                <BiPlusCircle />
              </Link>
            )}

            <AppDropdown
              value={selectedCashCenter}
              data={cashCenterResponse ? cashCenterResponse.data : null}
              textField="name"
              dataItemKey="cashCenterId"
              defaultItem={{ cashCenterId: "0", name: "Select Cash Center" }}
              onChange={onDdlCashCentersChanged}
              filterable={false}
            />
            <Input
              value={filter}
              onChange={onFilterChange}
              placeholder="Search"
            />
          </FilterBar>
          <Grid
            data={process(assetsList?.data.data || [], dataState)}
            {...dataState}
            onDataStateChange={(e) => {
              setDataState(e.dataState);
            }}
            filterable
            sortable
            pageable
            total={assetsList?.data.totalRecords || 0}
            pageSize={pageSize}
            skip={(currentPage - 1) * pageSize}
            onPageChange={onPageChange}
          >
            <Column field="assetNo" title="Asset Number" width={150} />
            <Column field="assetType" title="Asset Type" width={150} />
            <Column field="employeeId" title="Employee" width={150} />
            <Column field="description" title="Description" width={150} />
            <Column field="issuedBy" title="Issued By" width={150} />
            <Column field="issuedDate" title="Issue Date" width={150} />
            <Column field="receivedBy" title="Received By" width={150} />
            <Column field="model" title="Model" width={150} />
            <Column field="remarks" title="Remarks" width={150} />
            <Column field="returnedDate" title="Return Date" width={150} />
            <Column field="office" title="Office" width={150} />
            <Column field="routeNo" title="Route" width={150} />
            <Column field="serialNo" title="Serial No" width={150} />
            <Column field="status" title="Status" width={150} />
            {canEdit ? (
              <Column
                field="status"
                title="Edit"
                headerClassName="text-center"
                className="text-center"
                width={80}
                cell={editAssetCell}
              />
            ) : (
              <></>
            )}
            {canDelete ? (
              <Column
                field="status"
                title="Delete"
                headerClassName="text-center"
                className="text-center"
                width={80}
                cell={deleteAssetCell}
              />
            ) : (
              <></>
            )}
          </Grid>
        </CardBody>
      </Card>
    </Flex>
  );
}
